import { z } from 'zod';

import { BalanceHistoryItemId, LosId } from '../../BrandedIds';
import { type BalanceEvent } from '../../loan/Snapshots';
import { zodTemporalInstant } from '../../Temporal';
import { literalsCovering } from '../util/literalsCovering';
import { ExportableDeferredAmounts } from './ExportableDeferredAmounts';

const BALANCE_EVENT_TYPES = literalsCovering<BalanceEvent['type']>()({
  current: [
    'boarding',
    'payment',
    'disbursement',
    'draw',
    'payoff',
    'advance',
    'fee',
    'constructionToPermanent',
    'paymentDeferralWorkoutPlan',
    'principalModification',
    'flexModification',
    'writeoff',
    'liquidation',
    'transfer',
  ],
});

/**
 * One entry of a loan's balance history: the loan's full balance set as of one
 * balance-affecting event (payment, disbursement, payoff, modification, ...). The first
 * entry (`ordinal` 0) is always the boarding event. Reading the entry with the greatest
 * `transactionDate` at or before a target date gives the loan's balances as of that date.
 */
export const ExportableBalanceHistoryItem = z
  .object({
    id: BalanceHistoryItemId.describe(
      'Synthetic `<eventType>:<eventId>:<occurrence>` identifier, derived from the source event so it survives history recomputations (unlike `ordinal`); `occurrence` is 0 except when one event yields multiple entries.',
    ),
    loanId: LosId,
    ordinal: z
      .int()
      .describe(
        "Position in the loan's balance history, authoritative where transaction dates tie. The history is recomputed as events land, so ordinals can shift; use for ordering, not identity.",
      ),

    transactionDate: zodTemporalInstant,
    eventType: z.enum(BALANCE_EVENT_TYPES),
    eventId: z
      .string()
      .describe('Id of the source object that produced this entry (payment id, disbursement id, ...).'),

    outstandingBalance: z.number(),
    previousOutstandingBalance: z.number(),

    escrowBalance: z.number(),
    netEscrowBalance: z.number(),
    statementSuspenseBalance: z.number(),
    futureStatementSuspenseBalance: z.number(),
    unappliedSuspenseBalance: z.number(),
    reserveBalance: z.number().optional(),
    feeBalance: z.number().optional(),
    holdbackBalance: z.number().optional(),
    lossDraftBalance: z.number().optional(),
    feesOutstanding: z.number().optional().describe('Excludes deferred fees.'),
    feesOutstandingIncludingDeferred: z.number().optional(),
    advancesPrincipalOutstanding: z.number().optional(),
    advancesPrincipalOutstandingIncludingDeferred: z.number().optional(),
    principalPrepaymentBalance: z.number(),
    deferredAmounts: ExportableDeferredAmounts,
  })
  .meta({ title: 'BalanceHistoryItem' });
export type ExportableBalanceHistoryItem = z.infer<typeof ExportableBalanceHistoryItem>;
